import { useState } from "react";

import { ApiFehler } from "../api/client";
import { useStatusIndividuell } from "../api/hooks";
import type { Anlage } from "../api/typen";
import { useToast } from "../kontext/ToastKontext";
import { zeitpunkt } from "../lib/format";
import { Hinweis, Knopf, Modal } from "./Basis";

const MAX = 300;

/**
 * Individueller Status für Sonderfälle: ersetzt nach außen (Portal des Installateurs, Status-Link des Kunden)
 * den automatischen Text. Der Fortschritt läuft weiter automatisch.
 */
export function StatusIndividuell({ anlage }: { anlage: Anlage }) {
  const setzen = useStatusIndividuell(anlage.id);
  const toast = useToast();
  const [offen, setOffen] = useState(false);
  const [text, setText] = useState("");
  const [fehler, setFehler] = useState<string | null>(null);

  function oeffnen() {
    setText(anlage.status_individuell ?? "");
    setFehler(null);
    setOffen(true);
  }

  function speichern(wert: string | null) {
    setFehler(null);
    setzen.mutate(wert, {
      onSuccess: () => {
        setOffen(false);
        toast.erfolg(wert ? "Individueller Status gesetzt" : "Wieder automatischer Status");
      },
      onError: (e) => setFehler(e instanceof ApiFehler ? e.message : "Speichern fehlgeschlagen"),
    });
  }

  return (
    <>
      {anlage.status_individuell ? (
        <div className="mt-2 max-w-xl rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs">
          <p className="font-semibold text-amber-900">Individueller Status (sehen Installateur und Kunde)</p>
          <p className="mt-0.5 text-sm text-neutral-900">„{anlage.status_individuell}“</p>
          <p className="mt-1 text-amber-800">
            seit {anlage.status_individuell_am ? zeitpunkt(anlage.status_individuell_am) : "–"} ·{" "}
            <button type="button" className="font-semibold underline" onClick={oeffnen}>ändern</button> ·{" "}
            <button type="button" className="font-semibold underline" onClick={() => speichern(null)}>entfernen</button>
          </p>
        </div>
      ) : (
        <button type="button" className="mt-2 block text-xs font-semibold text-neutral-600 underline underline-offset-2" onClick={oeffnen}>
          Individuellen Status setzen…
        </button>
      )}

      {offen && (
        <Modal
          offen
          titel="Individueller Status"
          beiSchliessen={() => setOffen(false)}
          fuss={
            <>
              <Knopf onClick={() => setOffen(false)}>Abbrechen</Knopf>
              <Knopf variante="primaer" disabled={!text.trim()} laedt={setzen.isPending} onClick={() => speichern(text.trim())}>
                Speichern
              </Knopf>
            </>
          }
        >
          <div className="space-y-3 text-sm">
            <p className="text-neutral-600">
              Für Sonderfälle: Dieser Text ersetzt den automatischen Status im Portal des Installateurs und im Status-Link des
              Kunden. Der Fortschrittsbalken läuft weiter automatisch.
            </p>
            <textarea
              data-autofokus
              rows={3}
              maxLength={MAX}
              className="w-full rounded-lg border border-neutral-300 px-3 py-2"
              placeholder="z. B. Der Netzbetreiber fordert ein neues Datenblatt an – wir kümmern uns darum."
              value={text}
              onChange={(e) => setText(e.target.value)}
            />
            <p className="text-right text-xs text-neutral-400">{text.length}/{MAX}</p>
            <Hinweis art="warnung">Jeder mit dem Status-Link kann den Text lesen – bitte keine sensiblen Daten (IBAN, Geburtsdatum, Zählernummer).</Hinweis>
            {fehler && <Hinweis art="fehler">{fehler}</Hinweis>}
          </div>
        </Modal>
      )}
    </>
  );
}
